import { QuestionTypes } from '@showbie-socrative/socrative-utils';

import {
  ApiQuestionResponse,
  BaseActivityResponse,
  Student,
  StudentActivityResponse,
} from '~teacher/models/activities';
import { QuizQuestion } from '~teacher/models/quizzes';

import { indexStudentByUserUuid } from './normalizeStudent';

export function mapQuestionResponse({
  question,
  response,
}: {
  question: QuizQuestion;
  response: ApiQuestionResponse;
}): StudentActivityResponse {
  const type = question.type;
  const {
    hidden,
    id,
    isCorrect,
    team,
    questionId,
    selectedAnswerIds = [],
    textResponse = '',
    userUuid,
  } = response;
  const baseActivityResponse: BaseActivityResponse = {
    id: id,
    isAutograded: question.hasCorrectAnswer,
    isCorrect: isCorrect,
    hidden,
    userUuid,
    questionId,
    team,
    timestamp: new Date(Date.now()),
  };
  switch (type) {
    case QuestionTypes.FreeResponse: {
      return {
        type: QuestionTypes.FreeResponse,
        textResponse,
        ...baseActivityResponse,
      };
    }
    case QuestionTypes.MultipleChoice: {
      return {
        type: QuestionTypes.MultipleChoice,
        selectedAnswers: [...selectedAnswerIds],
        ...baseActivityResponse,
      };
    }
    case QuestionTypes.TrueFalse: {
      return {
        type: QuestionTypes.TrueFalse,
        selectedAnswer: selectedAnswerIds[0],
        ...baseActivityResponse,
      };
    }
  }
}

export const indexResponsesByQuestionId = (
  responses: Record<string, ApiQuestionResponse>
) =>
  Object.values<ApiQuestionResponse>(responses).reduce<
    Record<string, Set<number | string>>
  >((accumulator, currentValue) => {
    const { questionId, id } = currentValue;
    const set = accumulator[questionId] ?? new Set();
    accumulator[questionId] = set.add(id);
    return accumulator;
  }, {});

export const indexResponsesByStudentId = ({
  responses,
  students,
}: {
  responses: Record<string, ApiQuestionResponse>;
  students: Record<string, Student>;
}) =>
  Object.values<ApiQuestionResponse>(responses).reduce<
    Record<string, Set<number>>
  >((accumulator, currentValue) => {
    const { userUuid, id } = currentValue;
    const studentIds = Object.values<Student>(students)
      .filter((s) => s.userUuid === userUuid)
      .map((s) => s.id);
    if (studentIds.length > 0) {
      const [studentId] = studentIds;
      const set = accumulator[studentId] ?? new Set();
      accumulator[studentId] = set.add(id);
    }
    return accumulator;
  }, {});

export const combineResponsesQuestions = ({
  responses,
  quizQuestion,
}: {
  responses: Record<string, ApiQuestionResponse>;
  quizQuestion: Record<string, QuizQuestion>;
}) =>
  Object.entries<ApiQuestionResponse>({ ...responses }).reduce<
    Record<string, StudentActivityResponse>
  >((accumulator, [key, response]) => {
    const { questionId } = response;
    const question = quizQuestion[questionId];
    if (question) {
      const questionResponse = mapQuestionResponse({
        question,
        response,
      });
      accumulator[key] = { ...questionResponse };
    }
    return accumulator;
  }, {});

export const combineQuestionIdStudentId = ({
  questionId,
  studentId,
}: {
  questionId: number;
  studentId: number;
}) => `${questionId}-${studentId}`;

const indexResponsesByStudentIdQuestionId = ({
  responses,
  students,
}: {
  responses: Record<string, ApiQuestionResponse>;
  students: Record<string, Student>;
}) => {
  const indexedStudents = indexStudentByUserUuid(students);
  return Object.values(responses).reduce((accumulator, response) => {
    const { questionId, userUuid } = response;
    const index = combineQuestionIdStudentId({
      questionId,
      studentId: indexedStudents[userUuid],
    });
    const ids = accumulator[index] ?? [];
    accumulator[index] = [...ids, response.id];
    return accumulator;
  }, {} as Record<string, number[]>);
};

export const normalizeResponses = ({
  responses,
  quizQuestion,
  students,
}: {
  responses: Record<string, ApiQuestionResponse>;
  quizQuestion: Record<string, QuizQuestion>;
  students: Record<string, Student>;
}) => {
  return {
    entities: combineResponsesQuestions({ responses, quizQuestion }),
    byQuestionId: indexResponsesByQuestionId(responses),
    byStudentId: indexResponsesByStudentId({ responses, students }),
    byQuestionStudentId: indexResponsesByStudentIdQuestionId({
      responses,
      students,
    }),
  };
};

export type NormalizedResponse = ReturnType<typeof normalizeResponses>;
